import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/src/components/shad/card";
import api from "@/src/utils";
import apiRoutes from "@/src/apiRoutes";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { textVariants } from "@/src/components/Text/Text";
import { Switch } from "@mantine/core";
import Loader from "@/src/components/Loader";
import { useTranslation } from "react-i18next";

type NotificationTypes = {
  type: string;
  value: boolean;
};

const Notifications = () => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.settings.notifications.${key}`);
  const fetchNotStatus = async () => {
    const res = await api.post(apiRoutes.checkEnablers, {
      type: "notification_settings",
    });

    return res.data;
  };

  const {
    data: notData,
    refetch,
    isLoading,
  } = useQuery<NotificationSettings>({
    queryKey: ["fetch_not_status"],
    queryFn: fetchNotStatus,
  });
  const toggleNotification = async ({ type, value }: NotificationTypes) => {
    const res = await api.post(apiRoutes.checkNotifications, {
      type: type,
      [type]: value,
    });
    return res?.data;
  };

  const { mutate } = useMutation({
    mutationKey: ["toggle_notifications"],
    mutationFn: toggleNotification,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        refetch();
      }
    },
    onMutate: () => {
      toast.promise(fetchNotStatus, {
        loading: "Loading...",
        // success: () => {
        //   return `Changes saved successfully`;
        // },
        error: "Oops! Something went wrong",
      });
    },
  });

  if (isLoading) return <Loader />;

  return (
    <div className="grid gap-5">
      <Card>
        <CardHeader>
          <CardTitle>{trans("header")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <ul className=" divide-y divide-gray-200 dark:divide-grey-400">
              <li className="py-4 flex items-center justify-between">
                <div className="flex flex-col">
                  <p className={textVariants()}>
                    {trans("items.incomingFunds.title")}
                  </p>
                  <p className={textVariants({ variant: "desc", size: "sm" })}>
                    {trans("items.incomingFunds.desc")}
                  </p>
                </div>
                <Switch
                  checked={notData?.info?.incoming_crypto}
                  onChange={() =>
                    mutate({
                      type: "crypto_deposits",
                      value: !notData?.info?.incoming_crypto,
                    })
                  }
                />
              </li>
              <li className="py-4 flex items-center justify-between">
                <div className="flex flex-col">
                  <p className={textVariants()}>
                    {trans("items.withdrawal.title")}
                  </p>
                  <p className={textVariants({ variant: "desc", size: "sm" })}>
                    {trans("items.withdrawal.desc")}
                  </p>
                </div>
                <Switch
                  checked={notData?.info.withdraw_crypto}
                  onChange={() =>
                    mutate({
                      type: "crypto_withdrawals",
                      value: !notData?.info.withdraw_crypto,
                    })
                  }
                />
              </li>
              <li className="py-4 flex items-center justify-between">
                <div className="flex flex-col">
                  <p className={textVariants()}>
                    {trans("items.tradeSuccess.title")}
                  </p>
                  <p className={textVariants({ variant: "desc", size: "sm" })}>
                    {trans("items.tradeSuccess.desc")}
                  </p>
                </div>
                <Switch
                  checked={notData?.info?.trade_success}
                  onChange={() =>
                    mutate({
                      type: "trades",
                      value: !notData?.info?.trade_success,
                    })
                  }
                />
              </li>
              <li className="py-4 flex items-center justify-between">
                <div className="flex flex-col">
                  <p className={textVariants()}>{trans("items.login.title")}</p>
                  <p className={textVariants({ variant: "desc", size: "sm" })}>
                    {trans("items.login.desc")}
                  </p>
                </div>
                <Switch
                  checked={notData?.info?.login}
                  onChange={() =>
                    mutate({
                      type: "login",
                      value: !notData?.info?.login,
                    })
                  }
                />
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Notifications;
